import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import * as THREE from "three"
import {
  subscribeSignaling,
  applyRemoteSignal,
  csrfToken,
  newPeerId,
} from "../helpers/beam_signaling"
import { createHereMap, arrowIcon } from "../helpers/here_map_helpers"
import { createPanoramaViewer } from "../helpers/panorama_viewer"

// Subset of the robot's SPORT_CMD set, sent over the WebRTC data channel. The bridge
// translates these into sport-mode requests; see docs/beam/robot-protocol.md and
// docs/beam/protocol-contract.md (data-channel command envelope).
const SPORT_CMD = {
  stand_up: { name: "StandUp", api_id: 1004 },
  stand_down: { name: "StandDown", api_id: 1005 },
  balance: { name: "BalanceStand", api_id: 1002 },
  sit: { name: "Sit", api_id: 1009 },
  stop: { name: "StopMove", api_id: 1003 },
}
// Gamepad button index -> action, using the W3C "standard" mapping. Values are
// either a SPORT_CMD key or a special handler sentinel ("estop", "record",
// "snapshot_main", "snapshot_usb").
// Source of truth for the gamepad mapping — keep the glyphs in sync with
// LABELS in app/components/beam/gamepad_hint_component.rb (the on-button hints).
const BUTTON_CMD = {
  0: "stand_up", // A
  3: "stand_down", // Y
  2: "balance", // X
  1: "stop", // B
  4: "play_sound_lb", // LB — play the clip bound to the LB soundboard slot
  // The shoulder row is the camera cluster: LT/RT grab a frame off a live feed, RB takes
  // the real thing. ("Sit" is deliberately UI-only, kept off the gamepad entirely so it
  // can't be triggered by an accidental press — it is not what RB does.)
  5: "photo_360", // RB — full-res 23 MP Theta capture (NOT a frame grab); freezes the
  //                 360 tile for ~8s while the camera shoots and transfers.
  6: "snapshot_main", // LT — frame grab off the robot front feed
  7: "snapshot_usb", // RT — frame grab off the dock USB feed
  8: "record", // Back/Select
  9: "estop", // Start
  12: "play_sound_dpad_up", // D-pad Up — second soundboard slot
  13: "speed_fast", // D-pad Down — select Schnell (fast) gear
  14: "speed_fine", // D-pad Left — select Fein (precise) gear
  15: "play_sound_dpad_right", // D-pad Right — third soundboard slot
}
// Soundboard gamepad slots, in the order the star button cycles through them. A clip is
// bound to at most one slot and a slot holds at most one clip. Keep in sync with
// HOTKEY_SLOTS in app/components/beam/soundboard_component.rb (which renders the hint
// badges) and the "play_sound_*" entries in BUTTON_CMD above.
const SOUND_HOTKEY_SLOTS = ["lb", "dpad_up", "dpad_right"]
const MOVE = { name: "Move", api_id: 1008 }
// Soundboard playback. The bridge routes these to the robot's AudioHub
// (rt/api/audiohub/request); a clip is addressed by its robot-side unique_id.
// Mode-independent — a sound can play in any posture. See docs/beam/.
const AUDIO_CMD = {
  play: { name: "PlaySound", api_id: 1002 },
  stop: { name: "StopSound", api_id: 1003 },
}
// Speaker volume + head LED. The bridge routes these to the robot's VUI channel
// (rt/api/vui/request). SetVolume {volume: 0-10} (1003) is verified; SetBrightness
// {brightness: 0-10} (1005) and SetLedColor {color} (1007) are sweep-known — VERIFY
// on hardware. All mode-independent. See docs/beam/robot-protocol.md (VUI table).
const VUI_CMD = {
  setVolume: { name: "SetVolume", api_id: 1003 },
  setBrightness: { name: "SetBrightness", api_id: 1005 },
  setColor: { name: "SetLedColor", api_id: 1007 },
}
// Head-LED colours the bridge accepts (must match VUI_COLORS in the bridge).
const LED_COLORS = ["white", "red", "yellow", "blue", "green", "cyan", "purple"]
const VOLUME_DEBOUNCE = 150 // ms — coalesce a slider drag into ~one command
const MOTOR_HOT_C = 60 // °C — highlight a joint at/above this (bridge sees ~61° hot)

// Connection-status pill colours, keyed by RTCPeerConnection / signaling state.
// Presentational only — see setStatus().
const STATUS_COLORS = {
  connected: { pill: "bg-green-100 text-green-800", dot: "bg-green-500" },
  connecting: { pill: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  new: { pill: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  failed: { pill: "bg-red-100 text-red-800", dot: "bg-red-500" },
  disconnected: { pill: "bg-gray-100 text-gray-800", dot: "bg-gray-400" },
  closed: { pill: "bg-gray-100 text-gray-800", dot: "bg-gray-400" },
}
const STATUS_DEFAULT = { pill: "bg-gray-100 text-gray-800", dot: "bg-gray-400" }
const PILL_CLASSES = [
  "bg-green-100",
  "text-green-800",
  "bg-amber-100",
  "text-amber-800",
  "bg-red-100",
  "text-red-800",
  "bg-gray-100",
  "text-gray-800",
]
const DOT_CLASSES = ["bg-green-500", "bg-amber-500", "bg-red-500", "bg-gray-400"]
// Per-camera capture state, shown as a dot on that camera's top-bar feed toggle.
// "captured" = the frame is uploaded and hanging in the Ereignis form; "saved" = it is
// additionally stored against the control session as a Beam::ImageCapture. "busy" is not
// a failure — the robot already has a capture in flight and the operator should simply
// retry — so it gets a neutral grey rather than the red of a real error.
const CAPTURE_STATUS_COLORS = {
  capturing: ["bg-amber-400", "animate-pulse"],
  captured: ["bg-sky-400"],
  saved: ["bg-green-400"],
  busy: ["bg-gray-400"],
  failed: ["bg-red-500"],
}
// Must stay a superset of every class above: this is the remove-list that clears the
// previous state before the next one is applied.
const CAPTURE_STATUS_CLASSES = [
  "bg-amber-400",
  "animate-pulse",
  "bg-sky-400",
  "bg-green-400",
  "bg-gray-400",
  "bg-red-500",
]
const CAPTURE_STATUS_LINGER = 3000 // ms — how long a finished capture keeps its dot
const BATTERY_CHIP_CLASSES = [
  "bg-gray-100",
  "text-gray-700",
  "bg-green-100",
  "text-green-700",
  "bg-amber-100",
  "text-amber-700",
  "bg-red-100",
  "text-red-700",
]
const MOVE_INTERVAL = 100 // ms — resend velocity while the joystick/gamepad is active

// Operator teleop: holds the browser RTCPeerConnection, exchanges SDP/ICE with the
// bridge over the Beam::SignalingChannel, shows the video, and sends drive commands
// over the data channel. Rails serves the page + brokers signaling only.
export default class extends Controller {
  static targets = [
    "connectButton",
    "disconnectButton",
    "connectionStatus",
    "statusPill",
    "statusDot",
    "battery",
    "batteryChip",
    "charging",
    "mode",
    "cam360Chip",
    "cam360Battery",
    "cam360Charging",
    "orientation",
    "motorTemp",
    "motorTempsPanel",
    "motorTempCell",
    "video",
    "videoPlaceholder",
    "usbVideo",
    "usbVideoPlaceholder",
    "thetaVideo",
    "thetaViewer",
    "thetaVideoPlaceholder",
    "photoButton",
    "photoLabel",
    "audio",
    "listenButton",
    "talkButton",
    "fullscreenButton",
    "rotateUsbButton",
    "feedButton",
    "feedStatus",
    "captureAnnounce",
    "joystick",
    "joystickKnob",
    "gamepadStatus",
    "invertButton",
    "recordButton",
    "captureButton",
    "captureLabel",
    "uploadButton",
    "uploadLabel",
    "plateButton",
    "plateLabel",
    "plateInterval",
    "plateResults",
    "snapshotList",
    "speedSlider",
    "speedValue",
    "volumeValue",
    "volumeSlider",
    "soundItem",
    "soundStar",
    "soundHotkeyHint",
    "brightnessValue",
    "brightnessSlider",
    "fineButton",
    "fastButton",
    "gpsPanel",
    "gpsMap",
  ]
  static values = {
    startUrl: String,
    sessionsBase: String,
    channel: String,
    gamepadLabel: String,
    recordLabel: String,
    stopRecordLabel: String,
    captureLabel: String,
    stopCaptureLabel: String,
    savingCaptureLabel: String,
    captureSavedLabel: String,
    captureEmptyLabel: String,
    captureFailedLabel: String,
    captureSavedLocalLabel: String,
    uploadCapturesLabel: String,
    uploadingCapturesLabel: String,
    capturesUploadedLabel: String,
    plateLabel: String,
    plateScanningLabel: String,
    plateScanFailedLabel: String,
    plateNoPlateLabel: String,
    plateMatchLabel: String,
    plateNoMatchLabel: String,
    plateSendWarningLabel: String,
    plateWarningSentLabel: String,
    snapshotSavingLabel: String,
    snapshotSavedLabel: String,
    snapshotEmptyLabel: String,
    snapshotFailedLabel: String,
    snapshotRemoveLabel: String,
    imageCapturingLabel: String,
    imageCapturedLabel: String,
    imageSavedLabel: String,
    imageBusyLabel: String,
    imageFailedLabel: String,
    photoLabel: String,
    photoCapturingLabel: String,
    photoSavedLabel: String,
    photoBusyLabel: String,
    photoFailedLabel: String,
    listenLabel: String,
    muteLabel: String,
    fullscreenLabel: String,
    exitFullscreenLabel: String,
    iceServers: Array,
    statusLabels: Object,
    robotId: String,
    fineSpeedDefault: { type: Number, default: 0.3 },
    fastSpeedDefault: { type: Number, default: 1.0 },
  }

  connect() {
    this.resetState()
    this.loadSpeed()
    this.loadInvert()
    this.loadUsbRotation()
    this.loadFeeds()
    this.loadSoundHotkeys()
    this.boundUnload = () => this.teardown()
    this.boundGamepadConnected = (event) => this.onGamepadConnected(event)
    this.boundGamepadDisconnected = () => this.onGamepadDisconnected()
    this.boundFullscreenChange = () => this.refreshFullscreenUi()
    window.addEventListener("beforeunload", this.boundUnload)
    window.addEventListener("gamepadconnected", this.boundGamepadConnected)
    window.addEventListener("gamepaddisconnected", this.boundGamepadDisconnected)
    document.addEventListener("fullscreenchange", this.boundFullscreenChange)
    this.gamepadNoneText = this.hasGamepadStatusTarget ? this.gamepadStatusTarget.textContent : ""
    this.refreshGamepadStatus()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundUnload)
    window.removeEventListener("gamepadconnected", this.boundGamepadConnected)
    window.removeEventListener("gamepaddisconnected", this.boundGamepadDisconnected)
    document.removeEventListener("fullscreenchange", this.boundFullscreenChange)
    clearTimeout(this.volumeTimer)
    Object.values(this.captureStatusTimers ?? {}).forEach(clearTimeout)
    this.teardown()
  }

  resetState() {
    this.sessionId = null
    // Whether the session row was already DELETEd (explicit stop or unload beacon),
    // so teardown doesn't end it twice. Kept separate from sessionId, which must
    // survive teardown for the async clip upload.
    this.sessionEnded = false
    this.pc = null
    this.dataChannel = null
    this.subscription = null
    // This page's identity on the signaling channel (see helpers/beam_signaling.js).
    this.peerId = newPeerId()
    this.moveLoop = null
    this.gamepadLoop = null
    this.draggingPointers = new Set() // pointerIds currently dragging a stick (multitouch)
    this.prevButtons = []
    this.velocity = { x: 0, y: 0, z: 0 }
    // Per-axis gamepad invert — operator quick-fix for a controller whose axes read
    // reversed (e.g. a non-standard mapping). Gamepad input only; persisted per robot.
    this.gamepadInvert = { x: false, y: false, z: false }
    // Flip the dock USB camera feed 180° for upside-down mounts. Purely visual
    // (a rotate-180 class on the video element); persisted per robot.
    this.usbRotated = false
    // Which video feeds the bridge should relay to this browser. Operators turn a
    // feed off to save bandwidth on a constrained link; the bridge then stops that
    // feed's RTP (see {type:"feed"} in protocol-contract.md). Persisted per robot,
    // re-sent when the data channel (re)opens so a disabled feed survives reconnects.
    // Default to the robot camera only: each feed is re-encoded in software on the
    // dock (a constrained ARM CPU), so starting all three at once can saturate it and
    // stall video. The operator enables the dock USB / 360° cameras on demand, and
    // their choice is then remembered per robot (loadFeeds).
    this.feedsEnabled = { main: true, usb: false, theta: false }
    // Whether each feed's track has arrived yet — so re-enabling a feed hides its
    // placeholder (the frame resumes) while an enabled-but-not-yet-arrived feed keeps
    // showing "waiting for video".
    this.feedArrived = { main: false, usb: false, theta: false }
    // Soundboard clips (robot_audio_uuid) bound to the gamepad hotkeys — LB and the two
    // D-pad buttons the drive controls leave free. Client-side selection; persisted per
    // robot. A clip holds at most one slot, a slot at most one clip.
    this.soundHotkeys = { lb: null, dpad_up: null, dpad_right: null }
    // Per-camera capture state timers (see setCaptureStatus).
    this.captureStatusTimers = {}
    // Walking-speed sensitivity. The active gear (mode) scales the outgoing Move
    // velocity (see sendMove): "fine" is for slow, granular positioning and "fast"
    // for covering ground. Translation (move = x/y) and rotation (turn = z) are
    // tuned independently. Tunable live and persisted per robot.
    this.speed = {
      move: { fine: this.fineSpeedDefaultValue, fast: this.fastSpeedDefaultValue },
      turn: { fine: this.fineSpeedDefaultValue, fast: this.fastSpeedDefaultValue },
      mode: "fine",
    }
    this.mediaRecorder = null
    this.recordedChunks = []
    this.recordingStartedAt = null
    // On-demand point-cloud capture toggle (sent over the data channel; the bridge
    // records LiDAR and uploads the container). Status comes back as capture_status.
    this.capturing = false
    this.resetCaptureButton()
    // Client-side license-plate recognition. While lprActive, frames are grabbed
    // (auto on plateLoop and/or manual trigger), uploaded, and scanned by Rails.
    this.lprActive = false
    this.plateLoop = null
    this.plateScanInFlight = false
    this.resetPlateButton()
    // Sync the volume/brightness sliders to the robot's reported values once per
    // session, so live telemetry doesn't fight an operator mid-drag afterwards.
    this.volumeSynced = false
    this.brightnessSynced = false
  }

  // --- Start / stop (button actions) ---

  async start() {
    if (this.pc) return
    this.setStatus("connecting")
    try {
      const session = await this.startSession()
      this.sessionId = session.session_id
      this.sessionEnded = false
      this.openPeerConnection()
      // Wait for the ActionCable subscription to be confirmed before sending the
      // offer: ActionCable replays subscriptions on (re)connect but NOT one-shot
      // `perform` calls, so an offer performed while the socket is still connecting
      // is silently dropped and the bridge never receives it.
      await this.subscribeSignaling()
      await this.sendOffer()
      this.toggleButtons(true)
      this.startGamepadLoop()
    } catch (error) {
      console.error("[beam] start failed", error)
      this.setStatus("failed")
      this.teardown()
    }
  }

  async stop() {
    await this.endSession()
    this.teardown()
    this.setStatus("disconnected")
  }

  // --- HTTP session lifecycle ---

  async startSession() {
    const response = await fetch(this.startUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    })
    if (!response.ok) throw new Error(`start session failed: ${response.status}`)
    return response.json()
  }

  async endSession() {
    if (!this.sessionId || this.sessionEnded) return
    this.sessionEnded = true
    try {
      await fetch(`${this.sessionsBaseValue}/${this.sessionId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
    } catch (error) {
      console.error("[beam] end session failed", error)
    }
  }

  // Fire-and-forget variant for teardown paths (tab close, Turbo navigation,
  // failed start). An active session left in the DB blocks the robot for every
  // other account (StartSessionService 409), so it must be ended even when the
  // page is going away: `keepalive` lets the request outlive the unload, and
  // nothing awaits it because beforeunload won't wait anyway.
  endSessionBeacon() {
    if (!this.sessionId || this.sessionEnded) return
    this.sessionEnded = true
    fetch(`${this.sessionsBaseValue}/${this.sessionId}`, {
      method: "DELETE",
      keepalive: true,
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    }).catch(() => {})
  }

  // --- WebRTC ---

  openPeerConnection() {
    this.pc = new RTCPeerConnection({ iceServers: this.iceServersValue })
    this.dataChannel = this.pc.createDataChannel("commands")
    // The bridge pushes telemetry as {type:"status",payload:{battery,mode}} over
    // this same data channel (see bautask-beam-bridge docs/protocol-contract.md).
    this.dataChannel.addEventListener("message", (event) => this.onDataChannelMessage(event))
    // Re-apply the operator's per-feed bandwidth choices once the channel is open:
    // a feed left disabled stays disabled across reconnects/renegotiation.
    this.dataChannel.addEventListener("open", () => this.applyFeedsOnOpen())
    // Ask what is still spooled on the dock. Captures outlive the session that recorded
    // them, so an operator arriving fresh must be able to see — and upload — a capture
    // somebody else left behind.
    this.dataChannel.addEventListener("open", () => this.requestCaptureStatus())
    // Three recvonly video m-lines, in a fixed order the bridge binds positionally:
    // first = robot front camera, second = dock USB camera, third = dock Theta 360.
    // Keep the transceiver references so the track handler can tell the feeds apart by
    // identity.
    this.robotVideoTransceiver = this.pc.addTransceiver("video", { direction: "recvonly" })
    this.usbVideoTransceiver = this.pc.addTransceiver("video", { direction: "recvonly" })
    this.thetaVideoTransceiver = this.pc.addTransceiver("video", { direction: "recvonly" })
    // Audio sendrecv: receive the robot mic (Listen) AND send the operator mic for
    // push-to-talk (M3). Keep the sender so PTT can swap the mic track in once; after
    // that PTT just toggles track.enabled (mute), so there's no per-press renegotiation.
    this.audioTransceiver = this.pc.addTransceiver("audio", { direction: "sendrecv" })

    this.pc.addEventListener("track", (event) => {
      if (event.track.kind === "audio") {
        // Route the robot mic to its own (muted) sink; the operator unmutes via Listen.
        if (this.hasAudioTarget) this.audioTarget.srcObject = new MediaStream([event.track])
        return
      }
      // Video: route by transceiver identity (the bridge relays with replaceTrack, so
      // event.streams may be empty — wrap the track directly). A missing source never
      // fires a track event, so each panel keeps its placeholder until its feed arrives.
      const stream = new MediaStream([event.track])
      if (event.transceiver === this.usbVideoTransceiver) {
        if (!this.hasUsbVideoTarget) return
        this.usbVideoTarget.srcObject = stream
        this.feedArrived.usb = true
        if (this.hasUsbVideoPlaceholderTarget) this.usbVideoPlaceholderTarget.hidden = true
        return
      }
      if (event.transceiver === this.thetaVideoTransceiver) {
        // The Theta feed is equirectangular: wrap it on a sphere (three.js) so the
        // operator can drag to look around, rather than showing the distorted 2:1 frame.
        if (!this.hasThetaVideoTarget) return
        this.initThetaViewer(stream)
        this.feedArrived.theta = true
        if (this.hasThetaVideoPlaceholderTarget) this.thetaVideoPlaceholderTarget.hidden = true
        return
      }
      if (!this.hasVideoTarget) return
      this.videoTarget.srcObject = stream
      this.feedArrived.main = true
      if (this.hasVideoPlaceholderTarget) this.videoPlaceholderTarget.hidden = true
    })
    this.pc.addEventListener("icecandidate", (event) => {
      if (event.candidate) this.signal("ice", event.candidate.toJSON())
    })
    this.pc.addEventListener("connectionstatechange", () => this.setStatus(this.pc.connectionState))
  }

  async sendOffer() {
    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    this.signal("offer", { type: offer.type, sdp: offer.sdp })
  }

  // --- Signaling over ActionCable ---

  // Resolves once the channel subscription is confirmed by the server, so callers
  // can safely `perform` (e.g. send the SDP offer) without racing the WS handshake;
  // rejects when the channel refuses the session or nothing answers (see the helper).
  subscribeSignaling() {
    const { subscription, connected } = subscribeSignaling(this.channelValue, this.sessionId, {
      peer: this.peerId,
      received: (data) => this.onSignal(data),
    })
    this.subscription = subscription
    return connected
  }

  signal(type, payload) {
    if (this.subscription) this.subscription.perform("signal", { type, payload })
  }

  async onSignal(data) {
    if (!this.pc) return
    if (data.type === "replaced") {
      if (data.peer !== this.peerId) this.onReplaced()
      return
    }
    if (await applyRemoteSignal(this.pc, data)) return
    if (data.type === "status") this.applyStatus(data.payload || {})
  }

  // Another page of this account took the session over (the same drive opened in a
  // second tab or window). The session now belongs to that page: tear this one down
  // without ending the session — the beacon would end THEIR drive — and say why.
  onReplaced() {
    this.sessionEnded = true
    this.teardown()
    this.setStatus("replaced")
  }

  // Robot → browser telemetry arrives over the WebRTC data channel.
  onDataChannelMessage(event) {
    let data
    try {
      data = JSON.parse(event.data)
    } catch {
      return
    }
    if (data.type === "status") this.applyStatus(data.payload || {})
    else if (data.type === "capture_status") this.onCaptureStatus(data.payload || {})
    else if (data.type === "photo_status") this.onPhotoStatus(data.payload || {})
    else if (data.type === "feed_status") this.onFeedStatus(data.payload || {})
  }

  applyStatus(payload) {
    if (payload.battery != null && this.hasBatteryTarget)
      this.batteryTarget.textContent = `${payload.battery}%`
    if (payload.battery != null && this.hasBatteryChipTarget) this.colorBatteryChip(payload.battery)
    // Charging bolt. Key-presence check on purpose: the ActionCable status path pushes
    // payloads without this key, and an older bridge sends none at all — neither must
    // clear a bolt the data channel just set. null (unknown) reads as "not charging".
    if ("charging" in payload && this.hasChargingTarget)
      this.chargingTarget.hidden = payload.charging !== true
    if (payload.mode != null && this.hasModeTarget) this.modeTarget.textContent = payload.mode

    // --- M2 telemetry ---
    if (this.hasOrientationTarget && (payload.roll != null || payload.pitch != null))
      this.orientationTarget.textContent = `R ${this.fmtDeg(payload.roll)} P ${this.fmtDeg(payload.pitch)}`
    if (payload.motor_temp != null && this.hasMotorTempTarget)
      this.motorTempTarget.textContent = `${payload.motor_temp}°C`
    if (payload.motor_temps != null) this.updateMotorTemps(payload.motor_temps)

    // Initialise the audio/LED sliders to the robot's real values, once per session.
    if (!this.volumeSynced && payload.volume != null) {
      this.syncSlider(
        this.volumeSliderTarget,
        this.volumeValueTarget,
        payload.volume,
        "hasVolumeSliderTarget",
      )
      this.volumeSynced = true
    }
    if (!this.brightnessSynced && payload.brightness != null) {
      this.syncSlider(
        this.brightnessSliderTarget,
        this.brightnessValueTarget,
        payload.brightness,
        "hasBrightnessSliderTarget",
      )
      this.brightnessSynced = true
    }

    // Live position from the dock GPS dongle (null until a fix exists).
    if (payload.gps) this.updateGpsPosition(payload.gps)

    // The 360 camera's own battery (null for camera setups that don't report one).
    // Key-presence check on purpose: the ActionCable status path pushes payloads
    // without this key, and those must not touch the chip either way.
    if ("camera_360" in payload) this.updateCamera360(payload.camera_360)
  }

  // Show/hide + fill the 360-camera battery chip. The chip stays hidden until the
  // bridge supplies a reading (the GPS-panel convention), so a Theta over USB or an
  // older bridge simply never shows it. An explicit null after readings existed means
  // the camera stopped answering — hide again rather than display a stale value.
  updateCamera360(info) {
    if (!this.hasCam360ChipTarget) return
    if (info == null || info.battery == null) {
      this.cam360ChipTarget.hidden = true
      return
    }
    this.cam360ChipTarget.hidden = false
    if (this.hasCam360BatteryTarget) this.cam360BatteryTarget.textContent = `${info.battery}%`
    this.colorChip(this.cam360ChipTarget, info.battery)
    if (this.hasCam360ChargingTarget) this.cam360ChargingTarget.hidden = info.charging !== true
  }

  // Move (and on first fix, build) the live-position map from a {lat, lon, heading,
  // …} GPS payload. The map + HERE Maps SDK load lazily on the first fix so docks
  // without a GPS dongle never reveal the panel or fetch the maps script.
  updateGpsPosition(gps) {
    if (gps.lat == null || gps.lon == null || !this.hasGpsMapTarget) return
    const position = { lat: gps.lat, lng: gps.lon }

    if (!this.gpsMap) {
      this.pendingGps = gps
      if (this.gpsMapLoading) return
      this.gpsMapLoading = true
      if (this.hasGpsPanelTarget) this.gpsPanelTarget.hidden = false
      createHereMap(this.gpsMapTarget, { center: position, zoom: 18 })
        .then((ctx) => this.initGpsMap(ctx))
        .catch(() => {
          this.gpsMapLoading = false
        })
      return
    }

    this.gpsMarker.setGeometry(position)
    this.gpsMap.setCenter(position)
    if (gps.heading != null) this.gpsMarker.setIcon(arrowIcon("#22c55e", gps.heading))
  }

  initGpsMap({ H, map }) {
    const gps = this.pendingGps
    const position = { lat: gps.lat, lng: gps.lon }
    this.gpsMap = map
    this.gpsMarker = new H.map.Marker(position, { icon: arrowIcon("#22c55e", gps.heading || 0) })
    map.addObject(this.gpsMarker)
    this.gpsMapLoading = false
  }

  fmtDeg(value) {
    return value == null ? "–" : `${Math.round(value)}°`
  }

  // Show/hide the per-joint motor-temperature grid (driven by the motor-temp chip).
  toggleMotorTemps() {
    if (this.hasMotorTempsPanelTarget)
      this.motorTempsPanelTarget.hidden = !this.motorTempsPanelTarget.hidden
  }

  // Fill each grid cell from the bridge's per-joint map ({ "FR_hip": 41.2, … }); cells
  // address themselves by data-joint. A joint missing from the map shows "–"; hot joints
  // (≥ MOTOR_HOT_C) are highlighted red.
  updateMotorTemps(temps) {
    if (!this.hasMotorTempCellTarget) return
    for (const cell of this.motorTempCellTargets) {
      const value = temps[cell.dataset.joint]
      cell.textContent = value == null ? "–" : `${value}°C`
      cell.classList.toggle("text-red-600", value != null && value >= MOTOR_HOT_C)
      cell.classList.toggle("font-semibold", value != null && value >= MOTOR_HOT_C)
    }
  }

  syncSlider(slider, valueLabel, value, hasSliderFlag) {
    if (this[hasSliderFlag]) slider.value = String(value)
    if (valueLabel) valueLabel.textContent = String(value)
  }

  colorBatteryChip(level) {
    this.colorChip(this.batteryChipTarget, level)
  }

  colorChip(chip, level) {
    let colors = "bg-green-100 text-green-700"
    if (level <= 20) colors = "bg-red-100 text-red-700"
    else if (level <= 50) colors = "bg-amber-100 text-amber-700"
    chip.classList.remove(...BATTERY_CHIP_CLASSES)
    chip.classList.add(...colors.split(" "))
  }

  // --- Commands ---

  command(event) {
    const cmd = SPORT_CMD[event.currentTarget.dataset.command]
    if (cmd) this.sendCommand(cmd.name, cmd.api_id, "")
  }

  playSound(event) {
    const uuid = event.currentTarget.dataset.soundUuid
    if (!uuid) return
    this.sendCommand(AUDIO_CMD.play.name, AUDIO_CMD.play.api_id, { unique_id: uuid })
  }

  stopSound() {
    this.sendCommand(AUDIO_CMD.stop.name, AUDIO_CMD.stop.api_id, "")
  }

  // --- Soundboard gamepad hotkeys (LB, D-pad Up, D-pad Right) ---
  // Bind a clip to one of the three free gamepad buttons so it can be fired without the
  // mouse. Assignment is client-side, persisted per robot like the speed/invert settings.

  // Cycle the clicked clip through the slots: none -> LB -> D-pad Up -> D-pad Right ->
  // none. Taking a slot from another clip releases it there (one clip per slot).
  cycleSoundHotkey(event) {
    const uuid = event.currentTarget.dataset.soundUuid
    if (!uuid) return
    const current = this.soundHotkeyFor(uuid)
    const next = SOUND_HOTKEY_SLOTS[SOUND_HOTKEY_SLOTS.indexOf(current) + 1] ?? null
    if (current) this.soundHotkeys[current] = null
    if (next) this.soundHotkeys[next] = uuid
    this.applySoundHotkeyUi()
    this.saveSoundHotkeys()
  }

  // Play the clip bound to a slot. No-op when the slot is empty.
  playSoundHotkey(slot) {
    const uuid = this.soundHotkeys[slot]
    if (!uuid) return
    this.sendCommand(AUDIO_CMD.play.name, AUDIO_CMD.play.api_id, { unique_id: uuid })
  }

  // The slot a clip currently holds, or null. (indexOf(null) is -1, so an unbound clip
  // cycles to the first slot in cycleSoundHotkey.)
  soundHotkeyFor(uuid) {
    return SOUND_HOTKEY_SLOTS.find((slot) => this.soundHotkeys[slot] === uuid) ?? null
  }

  loadSoundHotkeys() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.soundHotkeyStorageKey()) ?? "null")
      if (saved && typeof saved === "object") {
        for (const slot of SOUND_HOTKEY_SLOTS) {
          if (typeof saved[slot] === "string") this.soundHotkeys[slot] = saved[slot]
        }
      } else {
        // Migrate the single-clip predecessor (one "active" sound, always LB) so an
        // operator's existing selection survives the upgrade.
        const legacy = JSON.parse(
          localStorage.getItem(`beam-teleop-active-sound:${this.robotIdValue}`) ?? "null",
        )
        if (typeof legacy === "string") this.soundHotkeys.lb = legacy
      }
    } catch {
      // ignore malformed/unavailable storage — fall back to no assignments
    }
    this.applySoundHotkeyUi()
  }

  saveSoundHotkeys() {
    try {
      localStorage.setItem(this.soundHotkeyStorageKey(), JSON.stringify(this.soundHotkeys))
    } catch {
      // storage may be unavailable (private mode) — assignments stay in-memory
    }
  }

  soundHotkeyStorageKey() {
    return `beam-teleop-sound-hotkeys:${this.robotIdValue}`
  }

  // Highlight every bound clip (ring + filled star) and reveal the badge of the button it
  // is bound to (all three are rendered hidden by the soundboard component).
  applySoundHotkeyUi() {
    if (!this.hasSoundItemTarget) return
    for (const item of this.soundItemTargets) {
      const slot = this.soundHotkeyFor(item.dataset.soundUuid)
      item.classList.toggle("ring-2", Boolean(slot))
      item.classList.toggle("ring-amber-400", Boolean(slot))
      // Lets flashGamepadAction highlight the clip when its button is pressed.
      item.dataset.gamepadAction = slot ? `play_sound_${slot}` : ""
      const star = item.querySelector('[data-beam-teleop-target="soundStar"]')
      if (star) {
        star.classList.toggle("text-amber-500", Boolean(slot))
        star.classList.toggle("text-gray-400", !slot)
        star.querySelector("ion-icon")?.setAttribute("name", slot ? "star" : "star-outline")
      }
      for (const hint of item.querySelectorAll('[data-beam-teleop-target="soundHotkeyHint"]')) {
        hint.classList.toggle("hidden", hint.dataset.hotkey !== slot)
      }
    }
  }

  setVolume(event) {
    const level = Math.round(Number(event.currentTarget.value))
    if (!Number.isFinite(level)) return
    if (this.hasVolumeValueTarget) this.volumeValueTarget.textContent = String(level)
    // Coalesce rapid slider input into ~one command per gesture. The bridge also
    // clamps to 0-10, so a stray value is harmless.
    clearTimeout(this.volumeTimer)
    this.volumeTimer = setTimeout(() => {
      this.sendCommand(VUI_CMD.setVolume.name, VUI_CMD.setVolume.api_id, { volume: level })
    }, VOLUME_DEBOUNCE)
  }

  setBrightness(event) {
    const level = Math.round(Number(event.currentTarget.value))
    if (!Number.isFinite(level)) return
    if (this.hasBrightnessValueTarget) this.brightnessValueTarget.textContent = String(level)
    // Same debounce as the volume slider; the bridge clamps to 0-10.
    clearTimeout(this.brightnessTimer)
    this.brightnessTimer = setTimeout(() => {
      this.sendCommand(VUI_CMD.setBrightness.name, VUI_CMD.setBrightness.api_id, {
        brightness: level,
      })
    }, VOLUME_DEBOUNCE)
  }

  setLedColor(event) {
    const color = event.currentTarget.dataset.color
    if (!LED_COLORS.includes(color)) return
    this.sendCommand(VUI_CMD.setColor.name, VUI_CMD.setColor.api_id, { color })
  }

  sendCommand(name, apiId, parameter) {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    this.dataChannel.send(
      JSON.stringify({ type: "command", payload: { name, api_id: apiId, parameter } }),
    )
  }

  // --- Point-cloud capture (on-demand) ---
  // Toggle a LiDAR capture: the bridge records voxel frames straight to a spool on the
  // dock and seals a container on stop. It does NOT upload there and then — that would
  // put tens of megabytes on the same uplink carrying this session's video. The upload
  // happens on the separate "Daten hochladen" button, or on its own once nobody is
  // connected. The button label/state follows the bridge's capture_status.
  toggleCapture() {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    const action = this.capturing ? "stop" : "start"
    if (action === "stop") this.setCaptureLabel(this.savingCaptureLabelValue, true)
    this.dataChannel.send(JSON.stringify({ type: "capture", payload: { action } }))
  }

  requestCaptureStatus() {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    this.dataChannel.send(JSON.stringify({ type: "capture", payload: { action: "status" } }))
  }

  // Ship whatever is waiting on the dock now.
  uploadCaptures() {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    this.setUploadLabel(this.uploadingCapturesLabelValue, true)
    this.dataChannel.send(JSON.stringify({ type: "capture", payload: { action: "upload" } }))
  }

  onCaptureStatus(payload) {
    // `state` is a frozen vocabulary; everything new rides in additive fields, so an
    // unrecognised `stage` must never turn a successful capture into an error here.
    this.updateUploadButton(payload)

    switch (payload.state) {
      case "recording":
      case "busy":
        this.capturing = true
        this.setCaptureLabel(this.stopCaptureLabelValue, false)
        break
      case "saving":
        this.setCaptureLabel(this.savingCaptureLabelValue, true)
        break
      case "saved":
        this.capturing = false
        // Two "saved" messages arrive per capture: first when the bytes are durable on
        // the dock, then again when Rails has the row. Say which one this is.
        this.setCaptureLabel(
          payload.stage === "uploaded"
            ? this.captureSavedLabelValue
            : this.captureSavedLocalLabelValue || this.captureSavedLabelValue,
          false,
        )
        break
      case "empty":
        this.capturing = false
        this.setCaptureLabel(this.captureEmptyLabelValue, false)
        break
      case "idle":
        // Not a capture outcome — a status/upload reply, or a stop with nothing running.
        // Reset rather than render "failed": nothing failed, and toggleCapture() has
        // already disabled the button, so leaving it alone would strand it.
        this.capturing = false
        this.resetCaptureButton()
        break
      default: // error | unsupported
        this.capturing = false
        this.setCaptureLabel(this.captureFailedLabelValue, false)
    }
  }

  // Show the upload button only when the dock actually has something to send, so it
  // never invites a click that would do nothing.
  updateUploadButton(payload) {
    if (!this.hasUploadButtonTarget) return
    if (payload.pending_count === undefined || payload.pending_count === null) return

    const pending = payload.pending_count > 0
    this.uploadButtonTarget.hidden = !pending
    if (!pending) {
      this.setUploadLabel(this.capturesUploadedLabelValue, true)
      return
    }
    const uploading = payload.stage === "queued" || payload.stage === "uploading"
    this.setUploadLabel(
      uploading ? this.uploadingCapturesLabelValue : this.uploadCapturesLabelValue,
      uploading,
    )
  }

  setUploadLabel(text, disabled) {
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.textContent = text
    if (this.hasUploadButtonTarget) this.uploadButtonTarget.disabled = disabled
  }

  resetCaptureButton() {
    if (this.hasCaptureLabelTarget) this.captureLabelTarget.textContent = this.captureLabelValue
    if (this.hasCaptureButtonTarget) this.captureButtonTarget.disabled = false
  }

  setCaptureLabel(text, disabled) {
    if (this.hasCaptureLabelTarget) this.captureLabelTarget.textContent = text
    if (this.hasCaptureButtonTarget) this.captureButtonTarget.disabled = disabled
  }

  // --- Theta 360 high-res capture (full-res OSC still via the bridge) ---
  // Distinct from the snapshot frame-grab: this triggers the camera's native 23 MP
  // capture (the live 360 feed briefly pauses while the Z1 switches modes). The bridge
  // uploads the image and reports progress as photo_status.
  capturePhoto() {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    // A 360 capture takes ~7s (the camera stitches 23 MP, then ships ~8 MB over Wi-Fi).
    // The on-screen button is disabled for the duration, but a gamepad press bypasses the
    // button entirely — so guard here, or mashing RB queues shots the bridge can only
    // answer with "busy".
    if (this.hasPhotoButtonTarget && this.photoButtonTarget.disabled) return
    this.setPhotoLabel(this.photoCapturingLabelValue, true)
    this.setCaptureStatus("theta", "capturing")
    this.dataChannel.send(JSON.stringify({ type: "photo", payload: { format: "jpeg" } }))
  }

  // The bridge's photo_status states map onto the same three-step capture indicator the
  // frame grabs use: shooting -> transferred -> stored.
  onPhotoStatus(payload) {
    switch (payload.state) {
      case "capturing":
        this.setPhotoLabel(this.photoCapturingLabelValue, true)
        this.setCaptureStatus("theta", "capturing")
        break
      case "saving":
        this.setPhotoLabel(this.photoCapturingLabelValue, true)
        this.setCaptureStatus("theta", "captured")
        break
      case "saved":
        this.setPhotoLabel(this.photoSavedLabelValue, false)
        this.setCaptureStatus("theta", "saved")
        break
      case "busy":
        this.setPhotoLabel(this.photoBusyLabelValue, false)
        this.setCaptureStatus("theta", "busy")
        break
      default: // error | unsupported
        this.setPhotoLabel(this.photoFailedLabelValue, false)
        this.setCaptureStatus("theta", "failed")
    }
  }

  setPhotoLabel(text, disabled) {
    if (this.hasPhotoLabelTarget) this.photoLabelTarget.textContent = text
    if (this.hasPhotoButtonTarget) this.photoButtonTarget.disabled = disabled
    // Revert a terminal status back to the default label after a moment.
    if (!disabled) {
      clearTimeout(this.photoStatusTimer)
      this.photoStatusTimer = setTimeout(() => {
        if (this.hasPhotoLabelTarget) this.photoLabelTarget.textContent = this.photoLabelValue
      }, 3000)
    }
  }

  // --- Theta 360 interactive viewer (three.js equirectangular sphere) ---
  // The bridge relays the Theta as a normal video track; its frames are an
  // equirectangular projection. We feed the (hidden) <video> into a VideoTexture and hand it
  // to the shared panorama viewer, which wraps it on an inside-out sphere so the operator can
  // drag to look around / scroll to zoom. The same viewer serves the document and
  // Baufortschritt galleries (gallery_controller.js) from a still image.
  initThetaViewer(stream) {
    const video = this.thetaVideoTarget
    video.srcObject = stream
    video.muted = true
    video.playsInline = true
    const playing = video.play()
    if (playing) playing.catch(() => {})
    if (this.thetaViewer || !this.hasThetaViewerTarget) return

    const texture = new THREE.VideoTexture(video)
    texture.colorSpace = THREE.SRGBColorSpace
    this.thetaViewer = createPanoramaViewer(this.thetaViewerTarget, { texture })
  }

  destroyThetaViewer() {
    if (!this.thetaViewer) return
    this.thetaViewer.destroy()
    this.thetaViewer = null
  }

  // --- License-plate recognition (client-side LPR) ---
  // The browser grabs a still from the main feed (auto on a timer and/or on a manual
  // trigger), direct-uploads it, and POSTs the signed_id to /plate_scans. Rails runs
  // the AI read + fleet match and returns the result, which we render in a small list.
  // No bridge involvement — Rails owns the AI flow. Unknown vehicles get a one-click
  // "send warning email" action.

  // Toggle LPR on/off. While on, an interval > 0 auto-scans every N seconds; a manual
  // trigger (the "Jetzt scannen" button or gamepad LT) scans on demand regardless.
  togglePlateRecognition() {
    this.lprActive = !this.lprActive
    this.applyPlateUi()
    if (this.lprActive) this.startPlateAutoScan()
    else this.stopPlateAutoScan()
  }

  startPlateAutoScan() {
    this.stopPlateAutoScan()
    const seconds = this.plateIntervalSeconds()
    if (seconds <= 0) return // 0 = manual only
    this.plateLoop = setInterval(() => this.scanPlateForCamera("main"), seconds * 1000)
  }

  stopPlateAutoScan() {
    if (!this.plateLoop) return
    clearInterval(this.plateLoop)
    this.plateLoop = null
  }

  // Re-arm the auto-scan timer when the operator changes the interval input.
  setPlateInterval() {
    if (this.lprActive) this.startPlateAutoScan()
  }

  plateIntervalSeconds() {
    if (!this.hasPlateIntervalTarget) return 0
    const seconds = Math.round(Number(this.plateIntervalTarget.value))
    return Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  }

  // Grab the current main-camera frame and send it for plate recognition. Reuses the
  // same canvas capture + direct upload as the incident snapshot, but pipes the result
  // into the LPR endpoint instead of the Ereignis form. Action target: the "Jetzt
  // scannen" button (data-camera optional, defaults to the main feed).
  scanPlate(event) {
    return this.scanPlateForCamera(event?.currentTarget?.dataset?.camera || "main")
  }

  // Grab a frame from the chosen feed ("main" robot front / "usb" dock) and scan it.
  // Used by the manual button, the auto-scan timer, and the gamepad photo triggers.
  async scanPlateForCamera(camera) {
    if (!this.sessionId) return
    const source = this.snapshotSourceFor(camera)
    if (!this.videoReady(source.video)) return
    if (this.plateScanInFlight) return // don't pile up requests on a slow scan
    this.plateScanInFlight = true
    this.setPlateLabel(this.plateScanningLabelValue, false)
    try {
      const capture = await this.captureFrame(source.video, source.rotate180)
      const result = await this.postPlateScan(capture.signedId)
      this.handlePlateResult(result)
    } catch (error) {
      console.error("[beam] plate scan failed", error)
      this.flashPlateLabel(this.plateScanFailedLabelValue)
    } finally {
      this.plateScanInFlight = false
    }
  }

  async postPlateScan(signedId) {
    const response = await fetch(`${this.sessionsBaseValue}/${this.sessionId}/plate_scans`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ signed_id: signedId }),
    })
    return response.json()
  }

  handlePlateResult(result) {
    this.applyPlateUi()
    if (!result || result.status === "no_plate") {
      this.flashPlateLabel(this.plateNoPlateLabelValue)
      return
    }
    if (result.status === "error" || !result.id) {
      this.flashPlateLabel(this.plateScanFailedLabelValue)
      return
    }
    this.renderPlateResult(result)
  }

  // Prepend a row: matched ⇒ green "Im Fuhrpark: <name>"; unknown ⇒ amber + a button
  // to email the per-robot alert address (disabled once sent).
  renderPlateResult(result) {
    if (!this.hasPlateResultsTarget) return

    const row = document.createElement("div")
    row.className = `flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-sm ${
      result.matched
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-amber-200 bg-amber-50 text-amber-800"
    }`

    const left = document.createElement("div")
    left.className = "min-w-0"
    const plate = document.createElement("span")
    plate.className = "font-semibold"
    plate.textContent = result.value
    const status = document.createElement("span")
    status.className = "ml-2 text-xs"
    status.textContent = result.matched
      ? `${this.plateMatchLabelValue}${result.fleet?.name ? `: ${result.fleet.name}` : ""}`
      : this.plateNoMatchLabelValue
    left.append(plate, status)
    row.append(left)

    if (!result.matched) row.append(this.buildWarnButton(result))
    this.plateResultsTarget.prepend(row)
  }

  buildWarnButton(result) {
    const button = document.createElement("button")
    button.type = "button"
    button.className =
      "shrink-0 rounded-md bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
    button.textContent = this.plateSendWarningLabelValue
    if (result.warning_emailed) {
      button.disabled = true
      button.textContent = this.plateWarningSentLabelValue
    } else {
      button.addEventListener("click", () => this.sendPlateWarning(result.id, button))
    }
    return button
  }

  async sendPlateWarning(detectionId, button) {
    button.disabled = true
    try {
      const response = await fetch(
        `${this.sessionsBaseValue}/${this.sessionId}/plate_scans/${detectionId}/warn`,
        {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        },
      )
      const result = await response.json()
      if (response.ok && result.status === "sent") {
        button.textContent = this.plateWarningSentLabelValue
      } else {
        button.disabled = false
        button.title = result.error || ""
        this.flashPlateLabel(this.plateScanFailedLabelValue)
      }
    } catch (error) {
      console.error("[beam] plate warning failed", error)
      button.disabled = false
    }
  }

  resetPlateButton() {
    this.lprActive = false
    this.plateScanInFlight = false
    this.applyPlateUi()
  }

  // Highlight the toggle when active and set its label to the scanning/idle text.
  // Toggle the base bg/text classes OFF when active (not just layer indigo on top),
  // else Tailwind's source order can let the base bg-white win and the button looks
  // inactive. Mirrors highlightModeButton (the Fein/Schnell toggle).
  applyPlateUi() {
    if (this.hasPlateButtonTarget) {
      const button = this.plateButtonTarget
      button.classList.toggle("bg-indigo-600", this.lprActive)
      button.classList.toggle("text-white", this.lprActive)
      button.classList.toggle("border-indigo-600", this.lprActive)
      button.classList.toggle("bg-white", !this.lprActive)
      button.classList.toggle("text-gray-700", !this.lprActive)
      button.classList.toggle("border-gray-300", !this.lprActive)
      button.classList.toggle("hover:bg-gray-50", !this.lprActive)
      button.classList.toggle("hover:bg-indigo-700", this.lprActive)
      button.setAttribute("aria-pressed", String(this.lprActive))
    }
    this.setPlateLabel(this.lprActive ? this.plateScanningLabelValue : this.plateLabelValue, false)
  }

  setPlateLabel(text, disabled) {
    if (this.hasPlateLabelTarget) this.plateLabelTarget.textContent = text
    if (this.hasPlateButtonTarget) this.plateButtonTarget.disabled = disabled
  }

  // Briefly show a transient status on the label, then revert to the current state.
  flashPlateLabel(text) {
    this.setPlateLabel(text, false)
    clearTimeout(this.plateLabelTimer)
    this.plateLabelTimer = setTimeout(() => this.applyPlateUi(), 2500)
  }

  sendMove() {
    // Scale a copy by the active gear's factors — never mutate this.velocity,
    // which the joystick/gamepad rewrite each tick. Translation (x/y) and rotation
    // (z) use independent factors. 0 * f stays 0, so a released stick / E-Stop
    // still sends a clean full stop.
    const move = this.speed.move[this.speed.mode]
    const turn = this.speed.turn[this.speed.mode]
    const v = this.velocity
    this.sendCommand(MOVE.name, MOVE.api_id, {
      x: this.round(v.x * move),
      y: this.round(v.y * move),
      z: this.round(v.z * turn),
    })
  }

  // --- Walking-speed sensitivity (fine / fast) ---
  // Each slider carries data-group ("move"/"turn") and data-gear ("fine"/"fast");
  // the Fein/Schnell toggle picks the active gear applied to the live velocity.

  setSpeed(event) {
    const { group, gear } = event.currentTarget.dataset
    if (!this.speed[group] || (gear !== "fine" && gear !== "fast")) return
    this.speed[group][gear] = this.readSlider(event.currentTarget)
    this.applySpeedUi()
    this.saveSpeed()
  }

  setMode(event) {
    this.applyMode(event.currentTarget.dataset.mode)
  }

  // Switch the active gear from any source (on-screen toggle or gamepad D-pad).
  applyMode(mode) {
    if (mode !== "fine" && mode !== "fast") return
    this.speed.mode = mode
    this.applySpeedUi()
    this.saveSpeed()
  }

  // Slider is an integer percentage [5, 100]; store as a [0.05, 1.0] factor.
  readSlider(slider) {
    const pct = Math.min(100, Math.max(5, Number(slider.value) || 0))
    return Math.round(pct) / 100
  }

  loadSpeed() {
    try {
      const raw = localStorage.getItem(this.speedStorageKey())
      if (raw) {
        const saved = JSON.parse(raw)
        for (const group of ["move", "turn"]) {
          for (const gear of ["fine", "fast"]) {
            const factor = saved?.[group]?.[gear]
            if (Number.isFinite(factor)) this.speed[group][gear] = factor
          }
        }
        if (saved.mode === "fine" || saved.mode === "fast") this.speed.mode = saved.mode
      }
    } catch {
      // ignore malformed/unavailable storage — fall back to defaults
    }
    this.applySpeedUi()
  }

  saveSpeed() {
    try {
      localStorage.setItem(this.speedStorageKey(), JSON.stringify(this.speed))
    } catch {
      // storage may be unavailable (private mode) — settings stay in-memory
    }
  }

  speedStorageKey() {
    return `beam-teleop-speed:${this.robotIdValue}`
  }

  // Sync sliders, percentage labels, and the active-gear toggle to this.speed.
  applySpeedUi() {
    if (this.hasSpeedSliderTarget) {
      for (const slider of this.speedSliderTargets) {
        const { group, gear } = slider.dataset
        slider.value = Math.round(this.speed[group][gear] * 100)
      }
    }
    if (this.hasSpeedValueTarget) {
      for (const label of this.speedValueTargets) {
        const { group, gear } = label.dataset
        label.textContent = `${Math.round(this.speed[group][gear] * 100)}%`
      }
    }
    if (this.hasFineButtonTarget)
      this.highlightModeButton(this.fineButtonTarget, this.speed.mode === "fine")
    if (this.hasFastButtonTarget)
      this.highlightModeButton(this.fastButtonTarget, this.speed.mode === "fast")
  }

  highlightModeButton(button, active) {
    button.classList.toggle("bg-green-500", active)
    button.classList.toggle("text-white", active)
    button.classList.toggle("bg-white", !active)
    button.classList.toggle("text-gray-700", !active)
  }

  // Halt the robot on two independent paths: the data channel (direct) and the
  // signaling channel (Rails -> bridge), so it stops even if the data channel is
  // degraded. The bridge treats "estop" as a force-stop.
  emergencyStop() {
    this.velocity = { x: 0, y: 0, z: 0 }
    this.stopMoveLoop()
    this.sendCommand("StopMove", 1003, "")
    this.sendCommand("Damp", 1001, "")
    this.signal("estop", {})
  }

  // --- Fullscreen (per video tile, via the Fullscreen API) ---

  // Toggle fullscreen on the [data-fullscreen] container that holds the clicked
  // button (one per video tile). object-contain letterboxes the feed on black.
  toggleFullscreen(event) {
    const container = event.currentTarget.closest("[data-fullscreen]")
    if (!container) return
    if (document.fullscreenElement) {
      document.exitFullscreen?.()
    } else {
      container.requestFullscreen?.()
    }
  }

  // Keep every fullscreen button's icon + label in sync with the current state.
  refreshFullscreenUi() {
    if (!this.hasFullscreenButtonTarget) return
    const active = Boolean(document.fullscreenElement)
    const icon = active ? "contract-outline" : "expand-outline"
    const label = active ? this.exitFullscreenLabelValue : this.fullscreenLabelValue
    this.fullscreenButtonTargets.forEach((button) => {
      button.querySelector("ion-icon")?.setAttribute("name", icon)
      if (label) {
        button.title = label
        button.setAttribute("aria-label", label)
      }
    })
  }

  // --- Recording (MediaRecorder → Active Storage direct upload) ---

  toggleRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop()
      return
    }
    const stream = this.hasVideoTarget ? this.videoTarget.srcObject : null
    if (!stream) return

    this.recordedChunks = []
    this.recordingStartedAt = new Date()
    this.mediaRecorder = new MediaRecorder(stream, { mimeType: "video/webm" })
    this.mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) this.recordedChunks.push(event.data)
    })
    this.mediaRecorder.addEventListener("stop", () => this.uploadRecording())
    this.mediaRecorder.start()
    this.setRecordingUi(true)
  }

  uploadRecording() {
    this.setRecordingUi(false)
    if (!this.recordedChunks.length || !this.sessionId) return

    const durationMs = Date.now() - this.recordingStartedAt.getTime()
    const recordedAt = this.recordingStartedAt.toISOString()
    const file = new File(this.recordedChunks, `beam-${this.sessionId}-${Date.now()}.webm`, {
      type: "video/webm",
    })
    new DirectUpload(file, "/rails/active_storage/direct_uploads").create((error, blob) => {
      if (error) {
        console.error("[beam] clip upload failed", error)
        return
      }
      this.postClip(blob.signed_id, recordedAt, durationMs)
    })
  }

  async postClip(signedId, recordedAt, durationMs) {
    await fetch(`${this.sessionsBaseValue}/${this.sessionId}/clips`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        clip: { signed_id: signedId, recorded_at: recordedAt, duration_ms: durationMs },
      }),
    })
  }

  setRecordingUi(recording) {
    if (!this.hasRecordButtonTarget) return
    this.recordButtonTarget.textContent = recording
      ? this.stopRecordLabelValue
      : this.recordLabelValue
    this.recordButtonTarget.classList.toggle("bg-red-600", recording)
    this.recordButtonTarget.classList.toggle("text-white", recording)
  }

  // --- Snapshot (grab the current video frame → Active Storage → incident form) ---
  // A WebRTC MediaStream is not canvas-tainted, so we can read the exact frame the
  // operator is looking at. Each capture direct-uploads a JPEG and is represented in
  // the Ereignis form as a hidden snapshots[] field + a removable preview thumbnail,
  // so the array of signed_ids rides along with the normal form POST.
  //
  // data-camera on the button selects the feed: "main" (robot front), "usb" (dock
  // camera), or "both" (one frame from each). Status is shown on the clicked button.
  captureSnapshot(event) {
    const button = event.currentTarget
    return this.captureForCamera(button?.dataset.camera || "main", button)
  }

  // Capture from one or more feeds and add each to the Ereignis form. `button` is the
  // on-screen control whose label reflects progress (the same camera button is reused
  // when a gamepad trigger fires the capture, so feedback lands in the right place).
  async captureForCamera(which, button) {
    const cameras = which === "both" ? ["main", "usb"] : [which]
    const sources = cameras.map((camera) => this.snapshotSourceFor(camera))

    if (sources.some((source) => !this.videoReady(source.video))) {
      this.setSnapshotStatus(button, this.snapshotEmptyLabelValue, false)
      cameras.forEach((camera) => this.setCaptureStatus(camera, "failed"))
      return
    }

    this.setSnapshotStatus(button, this.snapshotSavingLabelValue, true)
    cameras.forEach((camera) => this.setCaptureStatus(camera, "capturing"))
    try {
      const captures = await Promise.all(
        sources.map((source) => this.captureFrame(source.video, source.rotate180)),
      )
      captures.forEach((capture, index) => {
        this.addSnapshotPreview(capture.signedId, capture.objectUrl)
        // "captured": the frame is uploaded and in the Ereignis form. It only becomes
        // "saved" once the session copy lands (see persistSessionSnapshot).
        this.setCaptureStatus(cameras[index], "captured")
        this.persistSessionSnapshot(cameras[index], capture)
      })
      this.setSnapshotStatus(button, this.snapshotSavedLabelValue, false)
    } catch (error) {
      console.error("[beam] snapshot upload failed", error)
      this.setSnapshotStatus(button, this.snapshotFailedLabelValue, false)
      cameras.forEach((camera) => this.setCaptureStatus(camera, "failed"))
    }
  }

  // Resolve a camera key to its video element and whether its frame needs flipping
  // (the USB feed is rotated purely in CSS, so the raw frame must be rotated to match).
  snapshotSourceFor(camera) {
    if (camera === "usb") {
      return {
        video: this.hasUsbVideoTarget ? this.usbVideoTarget : null,
        rotate180: !!this.usbRotated,
      }
    }
    if (camera === "theta") {
      // The hidden Theta <video> holds the live equirectangular frame; a quick grab of
      // it is the instant low-effort companion to the full-res capturePhoto().
      return { video: this.hasThetaVideoTarget ? this.thetaVideoTarget : null, rotate180: false }
    }
    return { video: this.hasVideoTarget ? this.videoTarget : null, rotate180: false }
  }

  videoReady(video) {
    return !!(video && video.srcObject && video.videoWidth && video.videoHeight)
  }

  // Draw the current frame to a canvas, direct-upload it as a JPEG, and resolve with
  // the Active Storage signed_id plus a local object URL for the preview thumbnail.
  captureFrame(video, rotate180) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas")
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext("2d")
      if (rotate180) {
        ctx.translate(canvas.width, canvas.height)
        ctx.rotate(Math.PI)
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("snapshot blob unavailable"))
            return
          }
          const file = new File([blob], `beam-snapshot-${Date.now()}.jpg`, { type: "image/jpeg" })
          new DirectUpload(file, "/rails/active_storage/direct_uploads").create(
            (error, uploaded) => {
              if (error) {
                reject(error)
                return
              }
              resolve({
                signedId: uploaded.signed_id,
                objectUrl: URL.createObjectURL(blob),
                file,
                width: canvas.width,
                height: canvas.height,
              })
            },
          )
        },
        "image/jpeg",
        0.9,
      )
    })
  }

  // Append a thumbnail + hidden snapshots[] input to the incident form. The remove
  // button drops both, so an operator can retake before submitting.
  addSnapshotPreview(signedId, objectUrl) {
    if (!this.hasSnapshotListTarget) return

    const item = document.createElement("div")
    item.className = "relative h-16 w-16 overflow-hidden rounded border border-gray-200"

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "beam_incident[snapshots][]"
    input.value = signedId

    const img = document.createElement("img")
    img.src = objectUrl
    img.className = "h-full w-full object-cover"

    const remove = document.createElement("button")
    remove.type = "button"
    remove.title = this.snapshotRemoveLabelValue
    remove.setAttribute("aria-label", this.snapshotRemoveLabelValue)
    remove.className =
      "absolute right-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-xs font-bold leading-none text-white hover:bg-black/80"
    remove.textContent = "×"
    remove.addEventListener("click", () => {
      URL.revokeObjectURL(objectUrl)
      item.remove()
    })

    item.append(input, img, remove)
    this.snapshotListTarget.append(item)
  }

  // Also persist the grabbed frame against the control session (Beam::ImageCapture) so
  // it's retrievable later from the session history, regardless of whether an incident
  // is filed. A second, independent direct-upload keeps this decoupled from the
  // incident copy (removing a preview above never discards the session copy).
  // Best-effort: a failure only logs and never disrupts the snapshot/incident UX.
  persistSessionSnapshot(camera, capture) {
    if (!this.sessionId) return

    new DirectUpload(capture.file, "/rails/active_storage/direct_uploads").create((error, blob) => {
      if (error) {
        console.error("[beam] session snapshot upload failed", error)
        return
      }
      fetch(`${this.sessionsBaseValue}/${this.sessionId}/image_captures`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          image_capture: {
            signed_id: blob.signed_id,
            camera,
            width: capture.width,
            height: capture.height,
            captured_at: new Date().toISOString(),
          },
        }),
      })
        // Only now is the frame retrievable from the session history — that is what the
        // operator means by "saved". A rejected save leaves the dot at "captured".
        .then((response) => {
          if (response.ok) this.setCaptureStatus(camera, "saved")
          else console.error("[beam] session snapshot save rejected", response.status)
        })
        .catch((err) => console.error("[beam] session snapshot save failed", err))
    })
  }

  // --- Per-camera capture status (dot on the top-bar feed toggle) ---
  // The snapshot buttons that report progress live inside the video tiles, which are
  // hidden on PiP tiles and covered by the overlay widgets on the main tile — so a
  // capture fired from the gamepad had no visible outcome. The feed toggles are always
  // on screen, one per camera, which makes them the natural place for this.
  setCaptureStatus(feed, state) {
    const dot = this.hasFeedStatusTarget
      ? this.feedStatusTargets.find((el) => el.dataset.feed === feed)
      : null
    if (!dot) return

    const label = this.captureStatusLabel(state)
    dot.classList.remove(...CAPTURE_STATUS_CLASSES)
    dot.classList.add(...(CAPTURE_STATUS_COLORS[state] ?? CAPTURE_STATUS_COLORS.failed))
    dot.title = label
    dot.hidden = false
    // One live region serves all three cameras, so the camera has to be named: without it
    // a "both" capture writes the identical string twice and an unchanged live region is
    // not announced at all — the operator would hear about one camera and never know which.
    if (this.hasCaptureAnnounceTarget) {
      const camera = dot.dataset.label
      this.captureAnnounceTarget.textContent = camera ? `${camera} — ${label}` : label
    }

    // "capturing" stays until its outcome arrives; every other state is terminal for that
    // attempt and fades out so the toggles do not accumulate stale dots.
    clearTimeout(this.captureStatusTimers[feed])
    if (state === "capturing") return
    this.captureStatusTimers[feed] = setTimeout(() => {
      dot.hidden = true
      dot.title = ""
      dot.classList.remove(...CAPTURE_STATUS_CLASSES)
    }, CAPTURE_STATUS_LINGER)
  }

  captureStatusLabel(state) {
    if (state === "capturing") return this.imageCapturingLabelValue
    if (state === "captured") return this.imageCapturedLabelValue
    if (state === "saved") return this.imageSavedLabelValue
    if (state === "busy") return this.imageBusyLabelValue
    return this.imageFailedLabelValue
  }

  setSnapshotStatus(button, text, busy) {
    if (!button) return
    button.disabled = busy
    const label = button.querySelector("[data-snapshot-label]")
    if (!label) return
    // Remember each button's own default label (main/usb say "Foto", the combined
    // button says "Beide …") so the transient status can revert to the right one.
    if (!("defaultLabel" in label.dataset)) label.dataset.defaultLabel = label.textContent
    label.textContent = text
    if (!busy && text !== label.dataset.defaultLabel) {
      clearTimeout(button.snapshotStatusTimer)
      button.snapshotStatusTimer = setTimeout(() => {
        label.textContent = label.dataset.defaultLabel
      }, 2500)
    }
  }

  // --- On-screen joysticks (pointer, multitouch) ---
  // Two sticks: left = translate (up/down → x fwd/back, left/right → y strafe),
  // right = yaw (left/right → z). Pointer capture lets two thumbs drive both at once.

  joystickStart(event) {
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    this.draggingPointers.add(event.pointerId)
    this.startMoveLoop()
    this.joystickMove(event)
  }

  joystickMove(event) {
    if (!this.draggingPointers.has(event.pointerId)) return
    const stick = event.currentTarget
    const rect = stick.getBoundingClientRect()
    const radius = rect.width / 2
    let dx = event.clientX - (rect.left + radius)
    let dy = event.clientY - (rect.top + radius)
    const distance = Math.hypot(dx, dy)
    if (distance > radius) {
      dx = (dx / distance) * radius
      dy = (dy / distance) * radius
    }
    const knob = this.knobFor(stick)
    if (stick.dataset.stick === "right") {
      // Right stick: left/right = yaw (z); vertical unused.
      if (knob) knob.style.transform = `translate(${dx}px, 0px)`
      this.velocity.z = this.round(-dx / radius)
    } else {
      // Left stick: up = forward (x), right = strafe (y).
      if (knob) knob.style.transform = `translate(${dx}px, ${dy}px)`
      this.velocity.x = this.round(-dy / radius)
      this.velocity.y = this.round(dx / radius)
    }
  }

  joystickEnd(event) {
    if (!this.draggingPointers.delete(event.pointerId)) return
    const stick = event.currentTarget
    const knob = this.knobFor(stick)
    if (knob) knob.style.transform = "translate(0px, 0px)"
    if (stick.dataset.stick === "right") {
      this.velocity.z = 0
    } else {
      this.velocity.x = 0
      this.velocity.y = 0
    }
    this.sendMove() // reflect the released axes now (a full stop once both sticks idle)
    if (this.draggingPointers.size === 0) this.stopMoveLoop()
  }

  knobFor(stick) {
    return stick.querySelector("[data-beam-teleop-target='joystickKnob']")
  }

  startMoveLoop() {
    this.moveLoop ||= setInterval(() => this.sendMove(), MOVE_INTERVAL)
  }

  stopMoveLoop() {
    if (!this.moveLoop) return
    clearInterval(this.moveLoop)
    this.moveLoop = null
  }

  // --- Gamepad API ---

  startGamepadLoop() {
    this.gamepadLoop ||= setInterval(() => this.pollGamepad(), MOVE_INTERVAL)
  }

  // Prefer a controller the browser reports in the W3C "standard" mapping (its axes
  // match what pollGamepad assumes); fall back to the first pad so a non-standard
  // controller still works — the operator corrects it with the invert toggles.
  activeGamepad() {
    const pads = navigator.getGamepads?.() ?? []
    return [...pads].find((p) => p?.mapping === "standard") ?? [...pads].find((p) => p)
  }

  pollGamepad() {
    const pad = this.activeGamepad()
    if (!pad) return
    this.pollGamepadButtons(pad) // always, regardless of stick state
    // Raw axes per the standard mapping. The knob mirrors the PHYSICAL stick, so feed
    // it the raw values; the per-axis invert toggles only flip what is sent to the robot.
    const rawX = this.round(-this.deadzone(pad.axes[1] ?? 0)) // left stick Y → forward/back
    const rawY = this.round(this.deadzone(pad.axes[0] ?? 0)) // left stick X → strafe
    const rawZ = this.round(-this.deadzone(pad.axes[2] ?? 0)) // right stick X → yaw
    this.updateGamepadKnob(rawX, rawY, rawZ)
    const x = this.gamepadInvert.x ? -rawX : rawX
    const y = this.gamepadInvert.y ? -rawY : rawY
    const z = this.gamepadInvert.z ? -rawZ : rawZ
    const idle = x === 0 && y === 0 && z === 0
    if (idle && this.velocity.x === 0 && this.velocity.y === 0 && this.velocity.z === 0) return
    this.velocity = { x, y, z }
    this.sendMove()
  }

  // Mirror the physical sticks on the on-screen knobs (left = translate, right = yaw)
  // so a gamepad gives the same visual feedback as touch. Skipped while a pointer drag
  // owns the knobs. Inverse of joystickMove: left dx=y*r, dy=-x*r; right dx=-z*r.
  updateGamepadKnob(x, y, z) {
    if (this.draggingPointers.size > 0) return
    for (const stick of this.joystickTargets) {
      const knob = this.knobFor(stick)
      if (!knob) continue
      const radius = stick.offsetWidth / 2
      if (stick.dataset.stick === "right") {
        knob.style.transform = `translate(${-z * radius}px, 0px)`
      } else {
        knob.style.transform = `translate(${y * radius}px, ${-x * radius}px)`
      }
    }
  }

  // Fire a mapped action on the rising edge only, so holding a button triggers
  // once (not every poll tick) and the press that first exposes the gamepad to
  // the browser is absorbed as the initial state instead of firing.
  pollGamepadButtons(pad) {
    for (const [index, action] of Object.entries(BUTTON_CMD)) {
      const pressed = pad.buttons[index]?.pressed ?? false
      if (pressed && !this.prevButtons[index]) this.triggerGamepadAction(action)
      this.prevButtons[index] = pressed
    }
  }

  triggerGamepadAction(action) {
    this.flashGamepadAction(action)
    if (action === "estop") return this.emergencyStop()
    if (action === "record") return this.toggleRecording()
    // While LPR is active, the gamepad photo triggers feed the plate scanner (each its
    // own feed) instead of the Ereignis snapshot form — reuse the triggers the operator
    // already knows. With LPR off they take an incident photo as before.
    if (action === "snapshot_main")
      return this.lprActive ? this.scanPlateForCamera("main") : this.captureFromGamepad("main")
    if (action === "snapshot_usb")
      return this.lprActive ? this.scanPlateForCamera("usb") : this.captureFromGamepad("usb")
    // Deliberately does NOT follow LPR mode, unlike the two above: the 360 is the
    // documentation camera, and this is the one capture that cannot be reproduced later.
    // It shoots a full-res 360 whatever else the UI is doing. (A Theta's equirectangular
    // frame is a poor plate-scanner input anyway.)
    if (action === "photo_360") return this.capturePhoto()
    if (action.startsWith("play_sound_")) {
      return this.playSoundHotkey(action.replace("play_sound_", ""))
    }
    if (action === "speed_fine") return this.applyMode("fine")
    if (action === "speed_fast") return this.applyMode("fast")
    const cmd = SPORT_CMD[action]
    if (cmd) this.sendCommand(cmd.name, cmd.api_id, "")
  }

  // Fire a camera capture from a gamepad trigger, routing progress feedback to the
  // matching on-screen snapshot button (looked up by its data-camera).
  captureFromGamepad(camera) {
    this.captureForCamera(camera, this.element.querySelector(`[data-camera="${camera}"]`))
  }

  // Briefly highlight the matching on-screen control so a gamepad press gives the
  // same feedback as a mouse click. Posture buttons carry data-command; the other
  // mapped controls (snapshots/record/estop) carry data-gamepad-action. A ring is
  // used so the highlight reads on dark video-overlay and red buttons alike.
  flashGamepadAction(action) {
    this.element
      .querySelectorAll(`[data-command="${action}"], [data-gamepad-action="${action}"]`)
      .forEach((el) => {
        el.classList.add("ring-2", "ring-green-400")
        setTimeout(() => el.classList.remove("ring-2", "ring-green-400"), 200)
      })
  }

  deadzone(value, threshold = 0.15) {
    return Math.abs(value) < threshold ? 0 : value
  }

  // Normalised velocity component, rounded to 2 decimals so we don't stream
  // excessive precision over the data channel.
  round(value) {
    return Math.round(value * 100) / 100
  }

  // The browser only exposes a gamepad after a button press; surface its name when
  // it connects (and on load if already exposed) so the operator gets feedback.
  refreshGamepadStatus() {
    const pad = this.activeGamepad()
    if (pad) this.showGamepad(pad.id)
  }

  onGamepadConnected(event) {
    // A non-"standard" mapping is the usual cause of reversed gamepad axes. Warn (only
    // then), with id/mapping/axes, so the operator knows the invert toggles may be needed.
    const { id, mapping, axes } = event.gamepad
    if (mapping !== "standard") {
      console.warn("[beam] gamepad reports a non-standard mapping; axes may read reversed", {
        id,
        mapping,
        axes: Array.from(axes),
      })
    }
    this.showGamepad(event.gamepad.id)
  }

  onGamepadDisconnected() {
    if (this.hasGamepadStatusTarget) this.gamepadStatusTarget.textContent = this.gamepadNoneText
  }

  showGamepad(id) {
    if (this.hasGamepadStatusTarget) {
      this.gamepadStatusTarget.textContent = `${this.gamepadLabelValue}: ${id}`
    }
  }

  // --- Gamepad axis invert (per-axis operator override) ---
  // Flip the sign of one gamepad axis (forward/back, strafe, or turn) when a
  // controller reads reversed. Applies to the gamepad only; the on-screen joystick
  // is never inverted. Persisted per robot, like the speed settings.

  toggleInvert(event) {
    const axis = event.currentTarget.dataset.axis
    if (!(axis in this.gamepadInvert)) return
    this.gamepadInvert[axis] = !this.gamepadInvert[axis]
    this.applyInvertUi()
    this.saveInvert()
  }

  loadInvert() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.invertStorageKey()) ?? "null")
      for (const axis of ["x", "y", "z"]) {
        if (typeof saved?.[axis] === "boolean") this.gamepadInvert[axis] = saved[axis]
      }
    } catch {
      // ignore malformed/unavailable storage — fall back to defaults
    }
    this.applyInvertUi()
  }

  saveInvert() {
    try {
      localStorage.setItem(this.invertStorageKey(), JSON.stringify(this.gamepadInvert))
    } catch {
      // storage may be unavailable (private mode) — settings stay in-memory
    }
  }

  invertStorageKey() {
    return `beam-teleop-invert:${this.robotIdValue}`
  }

  // Highlight each invert toggle that is active.
  applyInvertUi() {
    if (!this.hasInvertButtonTarget) return
    for (const button of this.invertButtonTargets) {
      const active = this.gamepadInvert[button.dataset.axis] === true
      button.classList.toggle("bg-amber-100", active)
      button.classList.toggle("text-amber-800", active)
      button.classList.toggle("bg-white", !active)
      button.classList.toggle("text-gray-600", !active)
    }
  }

  // --- USB camera 180° rotation (per-robot display override) ---
  // Flip the dock USB feed for cameras mounted upside-down. Purely visual: a
  // rotate-180 class on the video element, reapplied on connect so it survives
  // stream (re)attach. Persisted per robot, like the speed/invert settings.

  toggleUsbRotation() {
    this.usbRotated = !this.usbRotated
    this.applyUsbRotationUi()
    this.saveUsbRotation()
  }

  loadUsbRotation() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.usbRotationStorageKey()) ?? "null")
      if (typeof saved === "boolean") this.usbRotated = saved
    } catch {
      // ignore malformed/unavailable storage — fall back to default
    }
    this.applyUsbRotationUi()
  }

  saveUsbRotation() {
    try {
      localStorage.setItem(this.usbRotationStorageKey(), JSON.stringify(this.usbRotated))
    } catch {
      // storage may be unavailable (private mode) — setting stays in-memory
    }
  }

  usbRotationStorageKey() {
    return `beam-teleop-rotation-usb:${this.robotIdValue}`
  }

  applyUsbRotationUi() {
    if (this.hasUsbVideoTarget) this.usbVideoTarget.classList.toggle("rotate-180", this.usbRotated)
    if (!this.hasRotateUsbButtonTarget) return
    this.rotateUsbButtonTarget.setAttribute("aria-pressed", String(this.usbRotated))
    this.rotateUsbButtonTarget.classList.toggle("bg-green-600/80", this.usbRotated)
    this.rotateUsbButtonTarget.classList.toggle("bg-black/50", !this.usbRotated)
  }

  // --- Per-feed enable/disable (bandwidth) ---
  // Turn a camera feed (main/usb/theta) off to stop the bridge relaying its RTP to
  // this browser, saving bandwidth on a constrained link. Per session, no
  // renegotiation; the dock camera keeps running for other sessions. The bridge
  // acks with feed_status. Persisted per robot and re-applied when the channel opens.

  toggleFeed(event) {
    const feed = event.currentTarget.dataset.feed
    if (!(feed in this.feedsEnabled)) return
    this.feedsEnabled[feed] = !this.feedsEnabled[feed]
    this.applyFeedUi(feed)
    this.saveFeeds()
    this.sendFeed(feed, this.feedsEnabled[feed])
  }

  sendFeed(feed, enabled) {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    this.dataChannel.send(
      JSON.stringify({ type: "feed", payload: { feed, action: enabled ? "enable" : "disable" } }),
    )
  }

  // On (re)connect the bridge starts every feed enabled, so only the feeds the
  // operator turned off need a message to restore their state.
  applyFeedsOnOpen() {
    for (const feed of Object.keys(this.feedsEnabled)) {
      this.applyFeedUi(feed)
      if (!this.feedsEnabled[feed]) this.sendFeed(feed, false)
    }
  }

  onFeedStatus(payload) {
    const feed = payload.feed
    if (!(feed in this.feedsEnabled)) return
    if (payload.available === false) {
      // No source for this feed on the session — drop the control out of the way.
      this.disableFeedButton(feed)
      return
    }
    this.feedsEnabled[feed] = payload.enabled !== false
    this.applyFeedUi(feed)
  }

  loadFeeds() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.feedsStorageKey()) ?? "null")
      if (saved && typeof saved === "object") {
        for (const feed of Object.keys(this.feedsEnabled)) {
          if (typeof saved[feed] === "boolean") this.feedsEnabled[feed] = saved[feed]
        }
      }
    } catch {
      // ignore malformed/unavailable storage — fall back to all-enabled
    }
    for (const feed of Object.keys(this.feedsEnabled)) this.applyFeedUi(feed)
  }

  saveFeeds() {
    try {
      localStorage.setItem(this.feedsStorageKey(), JSON.stringify(this.feedsEnabled))
    } catch {
      // storage may be unavailable (private mode) — setting stays in-memory
    }
  }

  feedsStorageKey() {
    return `beam-teleop-feeds:${this.robotIdValue}`
  }

  feedButtonFor(feed) {
    if (!this.hasFeedButtonTarget) return null
    return this.feedButtonTargets.find((button) => button.dataset.feed === feed) ?? null
  }

  applyFeedUi(feed) {
    const enabled = this.feedsEnabled[feed]
    const button = this.feedButtonFor(feed)
    if (button) {
      button.setAttribute("aria-pressed", String(enabled))
      button.classList.toggle("bg-green-600/80", enabled)
      button.classList.toggle("bg-black/50", !enabled)
    }
    // Show the panel's placeholder while the feed is off. On re-enable, hide it only
    // if the feed had already arrived (its frame resumes); an enabled feed that never
    // arrived keeps showing "waiting for video".
    const placeholder = this.feedPlaceholderFor(feed)
    if (placeholder) placeholder.hidden = enabled && this.feedArrived[feed]
  }

  feedPlaceholderFor(feed) {
    if (feed === "main") return this.hasVideoPlaceholderTarget ? this.videoPlaceholderTarget : null
    if (feed === "usb") {
      return this.hasUsbVideoPlaceholderTarget ? this.usbVideoPlaceholderTarget : null
    }
    if (feed === "theta") {
      return this.hasThetaVideoPlaceholderTarget ? this.thetaVideoPlaceholderTarget : null
    }
    return null
  }

  disableFeedButton(feed) {
    const button = this.feedButtonFor(feed)
    if (!button) return
    button.disabled = true
    button.setAttribute("aria-pressed", "false")
  }

  // --- Robot microphone (listen) ---

  toggleListen() {
    if (!this.hasAudioTarget) return
    const audio = this.audioTarget
    audio.muted = !audio.muted
    // This click is the user gesture that lets the browser play un-muted audio.
    if (!audio.muted) audio.play().catch(() => {})
    this.setListenUi(!audio.muted)
  }

  setListenUi(listening) {
    if (!this.hasListenButtonTarget) return
    this.listenButtonTarget.textContent = listening ? this.muteLabelValue : this.listenLabelValue
    this.listenButtonTarget.classList.toggle("bg-green-600/80", listening)
    this.listenButtonTarget.classList.toggle("bg-black/50", !listening)
  }

  // --- Push-to-talk (operator mic -> robot speaker, M3) ---
  // Hold the button to talk. The mic is captured once (first press, a user gesture so
  // getUserMedia is allowed) and attached to the audio sender; further presses just
  // unmute it (track.enabled), so there's no renegotiation per press.

  async startTalk() {
    if (!this.pc || !this.audioTransceiver) return
    try {
      if (!this.micTrack) {
        this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.micTrack = this.micStream.getAudioTracks()[0]
        this.micTrack.enabled = false
        await this.audioTransceiver.sender.replaceTrack(this.micTrack)
      }
      this.micTrack.enabled = true
      this.setTalkUi(true)
    } catch (err) {
      // Permission denied / no device — surface it and leave PTT off.
      this.setTalkUi(false)
      if (this.hasTalkButtonTarget) this.talkButtonTarget.title = String(err?.name || err)
    }
  }

  stopTalk() {
    if (this.micTrack) this.micTrack.enabled = false
    this.setTalkUi(false)
  }

  setTalkUi(talking) {
    if (!this.hasTalkButtonTarget) return
    this.talkButtonTarget.setAttribute("aria-pressed", talking ? "true" : "false")
    this.talkButtonTarget.classList.toggle("bg-green-600/80", talking)
    this.talkButtonTarget.classList.toggle("bg-black/50", !talking)
  }

  stopMic() {
    this.stopTalk()
    if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop())
    this.micStream = null
    this.micTrack = null
    this.audioTransceiver = null
  }

  preventDefault(event) {
    event.preventDefault()
  }

  // --- Teardown + helpers ---

  teardown() {
    this.endSessionBeacon()
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") this.mediaRecorder.stop()
    this.stopMoveLoop()
    this.stopPlateAutoScan()
    this.resetPlateButton()
    if (this.gamepadLoop) {
      clearInterval(this.gamepadLoop)
      this.gamepadLoop = null
    }
    this.prevButtons = []
    this.joystickKnobTargets.forEach((knob) => {
      knob.style.transform = "translate(0px, 0px)"
    })
    this.draggingPointers.clear()
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    this.stopMic()
    this.dataChannel?.close()
    this.dataChannel = null
    this.pc?.close()
    this.pc = null
    this.robotVideoTransceiver = null
    this.usbVideoTransceiver = null
    this.thetaVideoTransceiver = null
    this.destroyThetaViewer()
    if (this.hasVideoTarget) this.videoTarget.srcObject = null
    if (this.hasUsbVideoTarget) this.usbVideoTarget.srcObject = null
    if (this.hasThetaVideoTarget) this.thetaVideoTarget.srcObject = null
    if (this.hasAudioTarget) {
      this.audioTarget.srcObject = null
      this.audioTarget.muted = true
    }
    this.setListenUi(false)
    if (this.hasVideoPlaceholderTarget) this.videoPlaceholderTarget.hidden = false
    if (this.hasUsbVideoPlaceholderTarget) this.usbVideoPlaceholderTarget.hidden = false
    if (this.hasThetaVideoPlaceholderTarget) this.thetaVideoPlaceholderTarget.hidden = false
    this.toggleButtons(false)
  }

  toggleButtons(connected) {
    if (this.hasConnectButtonTarget) this.connectButtonTarget.hidden = connected
    if (this.hasDisconnectButtonTarget) this.disconnectButtonTarget.hidden = !connected
  }

  setStatus(state) {
    if (this.hasConnectionStatusTarget) {
      this.connectionStatusTarget.textContent = this.statusLabelsValue[state] || state
    }
    const colors = STATUS_COLORS[state] || STATUS_DEFAULT
    if (this.hasStatusPillTarget) {
      this.statusPillTarget.classList.remove(...PILL_CLASSES)
      this.statusPillTarget.classList.add(...colors.pill.split(" "))
    }
    if (this.hasStatusDotTarget) {
      this.statusDotTarget.classList.remove(...DOT_CLASSES)
      this.statusDotTarget.classList.add(colors.dot)
    }
  }
}
